import {
  buildNetworkErrorReport,
  hasGraphQLErrors,
  type NetworkErrorReport,
  networkErrorSignature,
} from '@shared/networkError';
import type { HeaderLike } from '@shared/traceId';

type Reporter = (report: NetworkErrorReport) => void;
type OrgIdGetter = () => string | null | undefined;

const NETWORK_ERROR_WINDOW_MS = 30_000;
const recentNetworkErrors = new Map<string, number>();

let installed = false;
let originalFetch: typeof window.fetch | undefined;
let originalOpen: typeof XMLHttpRequest.prototype.open | undefined;
let originalSend: typeof XMLHttpRequest.prototype.send | undefined;

function isGraphQLEndpoint(url?: string): boolean {
  if (!url) return false;
  try {
    return /\/graphql(\/|$)/i.test(new URL(url, window.location.href).pathname);
  } catch {
    return /\/graphql(\/|$|\?)/i.test(url);
  }
}

function getFetchUrl(input: RequestInfo): string {
  if (typeof input === 'string') return input;
  if (typeof URL !== 'undefined' && input instanceof URL) return input.href;
  return input.url;
}

async function getFetchRequestBody(input: RequestInfo, init?: RequestInit): Promise<unknown> {
  if (init && init.body !== undefined) return init.body;
  if (typeof Request !== 'undefined' && input instanceof Request) {
    try {
      return await input.clone().text();
    } catch {
      return undefined;
    }
  }
  return undefined;
}

function parseHeaders(rawHeaders: string): Record<string, string> {
  return rawHeaders
    .trim()
    .split(/[\r\n]+/)
    .filter(Boolean)
    .reduce((headers, line) => {
      const index = line.indexOf(':');
      if (index === -1) return headers;
      headers[line.slice(0, index).trim().toLowerCase()] = line.slice(index + 1).trim();
      return headers;
    }, {} as Record<string, string>);
}

function tryGetXhrResponseBody(xhr: XMLHttpRequest): unknown {
  try {
    if (!xhr.responseType || xhr.responseType === 'text') return xhr.responseText;
  } catch {
    // Accessing responseText can throw for non-text response types
  }
  return xhr.response;
}

function parseOperationName(body: unknown): string | undefined {
  if (typeof body !== 'string') {
    const record = body as Record<string, unknown> | undefined;
    return record && typeof record.operationName === 'string' ? record.operationName : undefined;
  }
  try {
    const parsed = JSON.parse(body) as Record<string, unknown>;
    return typeof parsed.operationName === 'string' ? parsed.operationName : undefined;
  } catch {
    return undefined;
  }
}

function reportOnce(report: NetworkErrorReport, reporter: Reporter): void {
  const sig = networkErrorSignature(report);
  const now = Date.now();
  const seen = recentNetworkErrors.get(sig);
  if (seen && now - seen < NETWORK_ERROR_WINDOW_MS) return;
  recentNetworkErrors.set(sig, now);
  for (const [k, t] of recentNetworkErrors) {
    if (t < now - NETWORK_ERROR_WINDOW_MS) recentNetworkErrors.delete(k);
  }
  reporter(report);
}

function maybeReport(input: {
  source: string;
  reporter: Reporter;
  orgIdGetter: OrgIdGetter;
  status: number;
  method?: string;
  requestUrl: string;
  requestBody?: unknown;
  responseBody?: unknown;
  headers?: HeaderLike;
}): void {
  if (!isGraphQLEndpoint(input.requestUrl)) return;
  // 401s are an expected part of the token-refresh lifecycle (access token
  // expires → refresh → retry) and are handled by the auth flow. Reporting them
  // here just creates Grafana noise, so skip them like the axios interceptors do.
  if (input.status === 401) return;
  if (input.status < 400 && !hasGraphQLErrors(input.responseBody)) return;

  const report = buildNetworkErrorReport({
    source: input.source,
    status: input.status,
    method: input.method,
    requestUrl: input.requestUrl,
    pageUrl: window.location.href,
    requestBody: input.requestBody,
    responseBody: input.responseBody,
    headers: input.headers,
    orgIdGetter: input.orgIdGetter,
    operationName: parseOperationName(input.requestBody),
  });

  reportOnce(report, input.reporter);
}

function patchFetch(source: string, reporter: Reporter, orgIdGetter: OrgIdGetter): void {
  if (!window.fetch) return;

  originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
    const response = await originalFetch!(input, init);
    const requestUrl = getFetchUrl(input);
    if (!isGraphQLEndpoint(requestUrl)) return response;

    try {
      const requestBody = await getFetchRequestBody(input, init);
      const responseBody = await response.clone().text();
      maybeReport({
        source,
        reporter,
        orgIdGetter,
        status: response.status,
        method:
          init && init.method
            ? init.method
            : typeof Request !== 'undefined' && input instanceof Request
            ? input.method
            : 'GET',
        requestUrl,
        requestBody,
        responseBody,
        headers: response.headers,
      });
    } catch (error) {
      console.error('Failed to capture fetch network error report', error);
    }

    return response;
  };
}

function patchXhr(source: string, reporter: Reporter, orgIdGetter: OrgIdGetter): void {
  originalOpen = XMLHttpRequest.prototype.open;
  originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    async?: boolean,
    username?: string | null,
    password?: string | null
  ) {
    (this as any).__bitwaveNetworkErrorCapture = {
      method,
      url: typeof url === 'string' ? url : url.href,
    };
    return originalOpen!.call(this, method, url, async === undefined ? true : async, username, password);
  };

  XMLHttpRequest.prototype.send = function (body?: Document | XMLHttpRequestBodyInit | null) {
    const meta = (this as any).__bitwaveNetworkErrorCapture as { method?: string; url?: string } | undefined;
    const requestBody = body === null ? undefined : body;

    if (meta && meta.url && isGraphQLEndpoint(meta.url)) {
      this.addEventListener('loadend', (event) => {
        const xhr = event.currentTarget as XMLHttpRequest;
        try {
          const responseBody = tryGetXhrResponseBody(xhr);
          maybeReport({
            source,
            reporter,
            orgIdGetter,
            status: xhr.status,
            method: meta.method,
            requestUrl: meta.url || window.location.href,
            requestBody,
            responseBody,
            headers: parseHeaders(xhr.getAllResponseHeaders()),
          });
        } catch (error) {
          console.error('Failed to capture XHR network error report', error);
        }
      });
    }

    return originalSend!.call(this, body as any);
  };
}

export function installNetworkErrorCapture(source: string, reporter: Reporter, orgIdGetter: OrgIdGetter): void {
  if (installed) return;
  installed = true;
  patchFetch(source, reporter, orgIdGetter);
  patchXhr(source, reporter, orgIdGetter);
}
